import React from "react";
import { Link } from "react-router-dom";
import PageShell, { Reveal, SectionHeading } from "../components/PageShell";
import { profile, highlights, resumes, languages, education } from "../data/profile";
import "../styles/About.scss";

const focusAreas = [
  {
    title: "Enterprise web",
    body: "Multi-tenant ERP, customs compliance (DJBC / CEISA 4.0), reporting and automated workflows.",
    stack: "CodeIgniter 4 · MariaDB · PHP",
  },
  {
    title: "AI & computer vision",
    body: "Face recognition, object segmentation and on-device models that still work offline.",
    stack: "Python · OpenCV · TensorFlow",
  },
  {
    title: "Mobile & full-stack JS",
    body: "Cross-platform apps and role-based web platforms with payments and authentication.",
    stack: "Flutter · React · Express",
  },
];

function About() {
  const facts = [
    { k: "Based in", v: profile.location },
    { k: "Currently", v: `${profile.current.role} — ${profile.current.company}` },
    { k: "Education", v: `${education.main.degree}, ${education.main.institution}` },
    { k: "Languages", v: languages.map((l) => `${l.name} (${l.level})`).join(" · ") },
  ];

  return (
    <PageShell slug="about">
      <section className="about-intro">
        <Reveal className="about-statement">
          <span className="eyebrow">Hello, I'm {profile.firstName}</span>
          <p>{profile.statement}</p>
        </Reveal>
        <Reveal className="about-summary" delay={0.1}>
          <p>{profile.summary}</p>
          <div className="btn-row">
            <a className="btn btn-primary" href={profile.cvFile} download>
              Download CV <span aria-hidden="true">↓</span>
            </a>
            <Link className="btn btn-ghost" to="/projects">
              See my work <span aria-hidden="true">→</span>
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="about-stats">
        {highlights.map((h, i) => (
          <Reveal key={h.label} className="about-stat" delay={i * 0.06}>
            <span className="stat-value">{h.value}</span>
            <span className="about-stat-label">{h.label}</span>
          </Reveal>
        ))}
      </section>

      <section>
        <SectionHeading eyebrow="What I do" title="Three areas I work in" />
        <div className="grid-3">
          {focusAreas.map((f, i) => (
            <Reveal key={f.title} className="card card-hover focus-card" delay={i * 0.08}>
              <span className="focus-no">0{i + 1}</span>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
              <span className="focus-stack">{f.stack}</span>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="grid-2 about-facts-wrap">
        <Reveal className="card about-facts">
          <span className="eyebrow">Quick facts</span>
          <dl>
            {facts.map((f) => (
              <div key={f.k}>
                <dt>{f.k}</dt>
                <dd>{f.v}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
        <Reveal className="card about-now" delay={0.1}>
          <span className="badge">
            <span className="pulse" /> Now
          </span>
          <h3>Building Qibris ERP KB</h3>
          <p>
            Leading full-stack development of a multi-tenant customs ERP for Indonesian bonded-zone
            companies — CEISA 4.0 Host-to-Host integration, traceable inventory and compliance-first
            business logic.
          </p>
          <Link to="/projects" className="text-link">
            Read the case study →
          </Link>
        </Reveal>
      </section>

      <section>
        <SectionHeading eyebrow="Resume" title="Pick the version you need">
          One general CV plus role-focused versions.
        </SectionHeading>
        <div className="resume-grid">
          {resumes.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.06}>
              <a
                className={`card card-hover resume-card ${r.primary ? "is-primary" : ""}`}
                href={r.url}
                {...(r.primary
                  ? { download: true }
                  : { target: "_blank", rel: "noopener noreferrer" })}
              >
                <div className="resume-top">
                  <span className="eyebrow">{r.primary ? "Latest · PDF" : "PDF"}</span>
                  <span className="resume-arrow" aria-hidden="true">
                    {r.primary ? "↓" : "↗"}
                  </span>
                </div>
                <h3>{r.title}</h3>
                <span className="resume-role">{r.role}</span>
                <p>{r.description}</p>
              </a>
            </Reveal>
          ))}
        </div>
      </section>
    </PageShell>
  );
}

export default About;
